import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import LoanCalculator from "../components/LoanCalculator";
import { 
  DollarSign, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Loader2,
  Wallet,
  FileText,
  TrendingUp,
  Building2,
  Smartphone,
  CreditCard,
  Landmark,
  Calculator
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const PRODUCT_ICONS = {
  working_capital: Wallet,
  invoice_discounting: FileText,
  merchant_cash_advance: CreditCard,
  wholesale_lending: Building2,
  term_loan: Landmark,
  home_equity: TrendingUp,
  device_finance: Smartphone
};

const LoanProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCalculator, setShowCalculator] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Filter out wholesale_lending and device_finance (they have standalone pages)
      const businessProducts = response.data.filter(
        p => p.id !== 'wholesale_lending' && p.id !== 'device_finance'
      );
      setProducts(businessProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="loan-products-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Tzur Finances" className="h-10" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/products" className="text-[#0A2540] font-medium">
                Business Loans
              </Link>
              <Link to="/wholesale-lending" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Microfinance Wholesale Loans
              </Link>
              <Link to="/individual-loans" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Individual Loans
              </Link>
              <Link to="/device-finance" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Device Finance
              </Link>
            </nav>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0A2540]">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#1e3a8a] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Business Loans
          </h1>
          <p className="text-gray-300 text-lg">
            From working capital to equipment financing, find the perfect loan product for your business
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, index) => {
            const IconComponent = PRODUCT_ICONS[product.id] || DollarSign;
            
            return (
              <Card 
                key={product.id}
                className="bg-white hover:border-[#0A2540] transition-all card-hover group"
                data-testid={`product-${product.id}`}
              >
                <CardHeader>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="h-12 w-12 rounded-lg bg-[#0A2540]/10 flex items-center justify-center group-hover:bg-[#0A2540] transition-colors">
                      <IconComponent className="h-6 w-6 text-[#0A2540] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-[#0A2540]">{product.name}</CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-gray-600">
                    {product.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Amount Range */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Amount</span>
                      </div>
                      <span className="font-mono font-semibold text-[#0A2540]">
                        ${product.min_amount.toLocaleString()} - ${product.max_amount.toLocaleString()}
                      </span>
                    </div>

                    {/* Term Range */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Term</span>
                      </div>
                      <span className="font-medium text-[#0A2540]">{product.term_range}</span>
                    </div>

                    {/* Interest Range */}
                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-amber-600" />
                        <span className="text-sm text-amber-700">Rate</span>
                      </div>
                      <span className="font-mono font-semibold text-amber-700">{product.interest_range}</span>
                    </div>

                    {/* Features */}
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Features</p>
                      <div className="space-y-2">
                        {product.features.map((feature, fIndex) => (
                          <div key={fIndex} className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA */}
                    <Link to="/products/apply" className="block">
                      <Button className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90 mt-4" data-testid={`apply-${product.id}`}>
                        Apply Now
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Comparison Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-[#0A2540]">Not Sure Which Product is Right for You?</h2>
            <p className="text-gray-600 mt-2">Our team can help you find the perfect financing solution</p>
          </div>
          
          <Card className="bg-[#0A2540] text-white">
            <CardContent className="p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">Get Personalized Recommendations</h3>
                <p className="text-gray-300">
                  Answer a few questions and our AI will suggest the best loan products for your business
                </p>
              </div>
              <Link to="/register">
                <Button className="bg-amber-500 text-[#0A2540] hover:bg-amber-400 whitespace-nowrap" data-testid="get-recommendations-btn">
                  Get Started
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Loan Calculator Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-[#0A2540] flex items-center justify-center gap-2">
              <Calculator className="h-6 w-6" />
              Loan Calculator
            </h2>
            <p className="text-gray-600 mt-2">
              Estimate your monthly payments before you apply
            </p>
          </div>
          
          <div className="max-w-xl mx-auto">
            <LoanCalculator 
              defaultAmount={25000}
              defaultTerm={12}
              loanType="business"
              onApply={(params) => {
                // Navigate to application with pre-filled amount
                navigate('/products/apply', { state: { amount: params.amount, term: params.term } });
              }}
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
          <p>© 2024 Tzur Finances. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LoanProducts;
